import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { MouseEvent, ReactElement, useCallback, useState } from 'react';
import scssVariables from '../../../../Config.module.scss';
import { EventActionsEnum, EventCategoriesEnum } from '../../../Services/Logging/DataLayerDefinitions';
import loggingService from '../../../Services/Logging/LoggingService';
import trackingService, { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import IconButton from '../Buttons/IconButton';
import styles from './Inputs.module.scss';

enum Location {
    Right = 'right',
    Left = 'left',
    Top = 'top',
    Center = 'center',
}

export interface INestedOption<T> {
    key: string;
    label: JSX.Element | string;
    value: T;
    options?: INestedOption<T>[];
    disabled?: boolean;
}

interface IIconMenuMultiStageDropdownProps<T> {
    name: string;
    iconButton: ReactElement;
    options: INestedOption<T>[];
    handleSelection: (value: T) => void;
    messageToDisplayAtTop?: string;
    buttonClassName?: string;
    tooltip?: string;
    testId?: string | undefined;
    track: TrackingEvent;
}

interface ISubMenuState<T> {
    anchor: HTMLElement;
    options: INestedOption<T>[];
    position: Location.Right | Location.Left;
    level: number;
}

const defaultSubMenuWidth = 100;
// eslint-disable-next-line @typescript-eslint/comma-dangle
const IconMenuMultiStageDropdown = <T,>(props: IIconMenuMultiStageDropdownProps<T>): JSX.Element => {
    const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
    const [subAnchorElement, setSubAnchorElement] = useState<null | HTMLElement>(null);
    const [subMenus, setSubMenus] = useState<ISubMenuState<T>[]>([]);
    const [menuExpandDirection, setMenuExpandDirection] = useState<Location.Right | Location.Left>(Location.Right);
    const [alignmentHelper, setAlignmentHelper] = useState<{ top: number }>({ top: 0 });
    const open = Boolean(anchorElement);

    const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        const target = event.currentTarget;
        const rect = target.getBoundingClientRect();

        setAlignmentHelper({ top: rect.top });
        setAnchorElement((previous) => (previous === target ? previous : target));
        setMenuExpandDirection(calculateMenuPosition(event.currentTarget));
    }, []);

    const handleClose = () => {
        loggingService.trackEventWithAnalytics(EventActionsEnum.DropdownSelection, EventCategoriesEnum.IconMenuClosed, '', props.name);
        setAnchorElement(null);
        setSubAnchorElement(null);
        setSubMenus([]);
        setMenuExpandDirection(Location.Right);
    };

    const handleOptionChange = (option: INestedOption<T>): void => {
        if (!option.value) {
            return;
        }

        props.handleSelection(option.value);
        handleClose();
        loggingService.trackEventWithAnalytics(EventActionsEnum.DropdownSelection, EventCategoriesEnum.IconMenuSelected, `${option.value}`, props.name);
        const eventValue = typeof option.label === 'string' ? option.label : option.key;
        trackingService.track({ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}Selected`, eventValue });
    };

    const getMaxDepth = (options: INestedOption<T>[]): number => {
        if (!options || options.length === 0) {
            return 0;
        }
        return 1 + Math.max(...options.map((option) => getMaxDepth(option.options ?? [])));
    };

    const calculateMenuPosition = (element: HTMLElement): Location.Right | Location.Left => {
        const rect = element.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const maxDepth = getMaxDepth(props.options);
        const totalWidthRequired = maxDepth * (defaultSubMenuWidth + rect.width);
        const spaceOnRight = viewportWidth - rect.right;
        const spaceOnLeft = rect.left;

        if (spaceOnRight < totalWidthRequired && spaceOnLeft > spaceOnRight) {
            return Location.Left;
        }

        return Location.Right;
    };

    const handleMenuItemClick = (event: MouseEvent<HTMLLIElement>, option: INestedOption<T>, level: number = 0) => {
        if (!option.options || !option.options.length) {
            handleOptionChange(option);
            return;
        }

        const element = event.currentTarget;
        setSubAnchorElement(element);
        const position = calculateMenuPosition(element);

        // Clear focus to prevent console warning
        element.blur();

        setSubMenus((previous) => [
            ...previous.filter((menu) => menu.level < level),
            {
                anchor: element,
                options: option.options!,
                position,
                level,
            },
        ]);
    };

    const renderMenuItem = (option: INestedOption<T>, index: number, level: number = 0, position: Location.Right | Location.Left = Location.Right) => {
        const hasSubOptions = option.options && option.options.length > 0;

        return (
            <MenuItem
                key={`${option.key}-${index}-${level}`}
                value={`${option.value}`}
                onClick={(event) => handleMenuItemClick(event, option, level + 1)}
                disabled={option.disabled}
                disableRipple
                disableTouchRipple
                sx={{
                    fontFamily: scssVariables.fontFamily,
                    display: 'flex',
                    justifyContent: position === Location.Left ? 'flex-start' : 'space-between',
                    alignItems: 'center',
                }}
                data-testid={`${props.testId}Option${option.key}Level${level}`}
            >
                {hasSubOptions && position === Location.Left && <ChevronLeft />}
                <span className={`${styles.multi_stage_dropdown_label} ${position === Location.Left ? styles.left_position : ''}`}>
                    {typeof option.label === 'string' ? option.label.charAt(0).toUpperCase() + option.label.slice(1) : option.label}
                </span>
                {hasSubOptions && position === Location.Right && <ChevronRight />}
            </MenuItem>
        );
    };

    const getMenuAnchorOrigin = (position: Location.Right | Location.Left) => {
        const expandRight = { vertical: Location.Top as const, horizontal: Location.Right as const };
        const expandLeft = { vertical: Location.Top as const, horizontal: Location.Left as const };

        return position === Location.Right ? expandRight : expandLeft;
    };

    const getMenuTransformOrigin = (position: Location.Right | Location.Left) => {
        const expandRight = { vertical: Location.Top as const, horizontal: Location.Left as const };
        const expandLeft = { vertical: Location.Top as const, horizontal: Location.Right as const };

        return position === Location.Right ? expandRight : expandLeft;
    };

    return (
        <div>
            <IconButton
                className={props.buttonClassName}
                handleClick={handleClick}
                tooltip={props.tooltip}
                testId={props.testId}
                track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}OpenClicked` }}
            >
                {props.iconButton}
            </IconButton>
            <Menu
                id="basic-menu"
                anchorEl={anchorElement}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
                sx={{
                    '& .MuiPaper-root': {
                        boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.3)',
                        border: `1px solid ${scssVariables.neutralColor}`,
                    },
                }}
                anchorOrigin={getMenuAnchorOrigin(menuExpandDirection)}
                transformOrigin={getMenuTransformOrigin(menuExpandDirection)}
                disableAutoFocusItem={false}
                transitionDuration={200}
            >
                {props.messageToDisplayAtTop && (
                    <MenuItem
                        value=""
                        disabled
                        disableRipple
                        disableTouchRipple
                        sx={{ color: scssVariables.mainTextColor, fontFamily: scssVariables.fontFamily }}
                        data-testid={`${props.testId}OptionDefault`}
                    >
                        <em>{props.messageToDisplayAtTop}</em>
                    </MenuItem>
                )}
                {props.options.map((option, index) => renderMenuItem(option, index, 0, menuExpandDirection))}
            </Menu>

            {subMenus.map((subMenu) => (
                <Menu
                    key={`sub-menu-${subMenu.level}-${subMenu.anchor.id || subMenu.anchor.tagName}-${subMenu.options[0]?.key || 'menu'}`}
                    id={`sub-menu-${subMenu.level}`}
                    anchorEl={subAnchorElement}
                    open
                    onClose={handleClose}
                    MenuListProps={{
                        'aria-labelledby': `sub-menu-button-${subMenu.level}`,
                    }}
                    sx={{
                        '& .MuiPaper-root': {
                            boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.3)',
                            border: `1px solid ${scssVariables.neutralColor}`,
                            top: `${alignmentHelper.top}px !important`,
                        },
                    }}
                    anchorOrigin={getMenuAnchorOrigin(menuExpandDirection)}
                    transformOrigin={getMenuTransformOrigin(menuExpandDirection)}
                    disableAutoFocusItem={false}
                    transitionDuration={200}
                >
                    {subMenu.options.map((option, index) => renderMenuItem(option, index, subMenu.level, subMenu.position))}
                </Menu>
            ))}
        </div>
    );
};

export default IconMenuMultiStageDropdown;
